// R-NAV-1 — Role-denial surface (spec §2.6, §global "بدون‌دسترسی").
//
// Rendered by AppShell when an authenticated user navigates to a
// workspace route their ACTIVE role may not see (route-roles.ts). The
// spec is explicit: a wrong-role visit must NOT redirect to /login (the
// user is signed in) — it shows a polite denial with a way back to their
// own role home. The api's 403 is the real gate; this is UX + defence.
//
// Uses the shared EmptyState primitive so the "no access" signature
// matches every other empty/blocked surface in the app.
import React from "react";
import { Icon } from "../icons";
import { Button, EmptyState } from "../ui";
import { useRole } from "../role";
import { useGo } from "../router";

export interface RoleDeniedProps {
  /** The route id the user tried to reach (shown for context). */
  routeId?: string;
}

export const RoleDenied = ({ routeId }: RoleDeniedProps): React.ReactElement => {
  const go = useGo();
  const { role, activeRoleId } = useRole();
  // Land back on the active role's home; fall back to the public home if
  // somehow there's no active role (shouldn't happen past the auth gate).
  const homeRoute = activeRoleId ? role.homeRoute : "home";

  return (
    <main
      id="role-denied"
      data-screen-label="403 · دسترسی محدود"
      className="page-shell"
      style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <EmptyState
        icon={<Icon name="shield" size={26} />}
        title="دسترسی به این بخش ندارید"
        body={
          <>
            این صفحه برای نقش شما در دسترس نیست. با نقش فعلی
            {role?.label ? ` («${role.label}»)` : ""} اجازهٔ مشاهدهٔ این بخش وجود ندارد.
            اگر فکر می‌کنید اشتباهی رخ داده، با مدیر سامانه تماس بگیرید.
          </>
        }
        cta={
          <Button variant="primary" size="md" onClick={() => go(homeRoute)}>
            بازگشت به خانهٔ من
          </Button>
        }
      />
    </main>
  );
};

export default RoleDenied;
